'use client';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useCalendarDayList } from 'calendar-blocks';

import ChevronLeftIcon from '../Icons/ChevronLeft';
import ChevronRightIcon from '../Icons/ChevronRight';

import { cn } from '@/lib/utils';
import { CalendarProps } from './Calendar';
import { VillageFutureBooking, VillagePriceVariants } from '@/services/types';
import useVillage from '@/hooks/useVillage';
import moment from 'moment';
import useVillageDates from '@/hooks/useVillageDates';
import Day from './Day';
import { DatePickerExtraDicts } from './Content';
import BookingDay from './BookingDay';

type Props = Omit<
  CalendarProps,
  'confirmedDates' | 'shouldFetchUnavailableDates'
> & {
  currentDate: Date;
  futureBookings?: VillageFutureBooking[];
  priceVariants?: VillagePriceVariants[];
  viewInfo: { month: number; year: number };
  setViewInfo: Dispatch<SetStateAction<{ month: number; year: number }>>;
  isNextMonth?: boolean;
  isOneDate?: boolean;
  limitDate?: Date;
  setLimitDate?: Dispatch<SetStateAction<Date | undefined>>;
  extraDicts?: DatePickerExtraDicts;
  showSimpleDay?: boolean;
  skipCheckInDaysValidation?: boolean;
  atPricePreview?: boolean;
};

const weekDaysMap: { [key: string]: string[] } = {
  en: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
  it: ['L', 'M', 'M', 'G', 'v', 'S', 'D'],
  de: ['M', 'D', 'M', 'D', 'F', 'S', 'S'],
  fr: ['L', 'M', 'M', 'J', 'V', 'S', 'D'],
};

export default function Month({
  currentDate,
  futureBookings,
  viewInfo,
  setViewInfo,
  rangeValue,
  onRangeValueChange,
  lang,
  isNextMonth,
  isOneDate = false,
  priceVariants,
  limitDate,
  setLimitDate,
  extraDicts,
  showSimpleDay,
  skipCheckInDaysValidation,
  atPricePreview,
}: Props) {
  const [lastDate, setLastDate] = useState<{ month: number; year: number }>();
  const [reachedLastAvailableMonth, setReachedLastAvailableMonth] =
    useState(false);

  const weekDays = weekDaysMap[lang];

  const { getDatePickerLastAvailableDate } = useVillage({});

  const { getDateInfos } = useVillageDates();

  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1,
  );

  const calendarMondayValue = 1;

  const monthDays = useCalendarDayList(
    viewInfo.month,
    viewInfo.year,
    calendarMondayValue,
  );

  const filteredMonthDays = monthDays.filter(
    (day) => day.isDifferentMonth == false,
  );

  const monthLabel = new Date(viewInfo.year, viewInfo.month).toLocaleDateString(
    lang,
    {
      month: 'long',
      year: 'numeric',
    },
  );

  function handleButtonDisable() {
    if (lastDate) {
      let lastAvailableMonth = lastDate.month;
      let lastAvailableYear = lastDate.year;

      let currentMonth = viewInfo.month + 1;

      let currentYear = viewInfo.year;

      if (currentMonth > 12) {
        currentMonth = currentMonth % 12;
        currentYear += Math.floor((viewInfo.month + 1) / 12);
        if (currentMonth === 0) {
          currentMonth = 12;
          currentYear -= 1;
        }
      }

      const atMobile = !isNextMonth;

      if (atMobile) {
        lastAvailableMonth -= 1;

        if (lastAvailableMonth === 0) {
          lastAvailableMonth = 12;
          lastAvailableYear -= 1;
        }
      }

      if (
        lastAvailableYear == currentYear &&
        lastAvailableMonth == currentMonth
      ) {
        return setReachedLastAvailableMonth(true);
      }
      if (reachedLastAvailableMonth) {
        setReachedLastAvailableMonth(false);
      }
    }
  }

  useEffect(() => {
    if (priceVariants) {
      const lastAvailableDate = getDatePickerLastAvailableDate(priceVariants);

      if (lastAvailableDate && !lastDate) {
        const formattedLastDate = moment(lastAvailableDate).add(5, 'hours');

        setLastDate({
          month: formattedLastDate.month() + 1,
          year: formattedLastDate.year(),
        });
      }
    }
    handleButtonDisable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewInfo]);

  return (
    <div
      className={cn('flex flex-col desktop:w-[310px]', {
        'hidden [@media(min-width:716px)]:flex': isNextMonth,
      })}>
      <div
        className={cn(
          'flex justify-between [@media(min-width:716px)]:justify-center items-center w-full',
          {
            'justify-center [@media(min-width:716px)]:justify-center':
              isNextMonth,
          },
        )}>
        {!isNextMonth ? (
          <button
            aria-label="Calendar previous month"
            className="[@media(min-width:716px)]:mr-auto"
            onClick={() => {
              const viewInfoDate = new Date(
                viewInfo.year,
                viewInfo.month - 1,
                1,
              );
              viewInfoDate.setHours(0, 0, 0, 0);
              if (viewInfoDate >= firstDayOfMonth) {
                setViewInfo((v) => ({ ...v, month: viewInfo.month - 1 }));
              }
            }}>
            <ChevronLeftIcon className="w-8 h-8 fill-current text-gray-500 cursor-pointer" />
          </button>
        ) : null}
        <span
          className={cn(
            'text-sm text-graphite font-bold [@media(min-width:716px)]:mr-auto',
            {
              '[@media(min-width:716px)]:ml-auto [@media(min-width:716px)]:mr-0':
                isNextMonth,
            },
          )}>
          {monthLabel.toLocaleUpperCase()}
        </span>
        <button
          aria-label="Calendar next month"
          disabled={reachedLastAvailableMonth}
          className={cn(!isOneDate && '[@media(min-width:716px)]:hidden', {
            '[@media(min-width:716px)]:flex ml-auto': isNextMonth,
          })}
          onClick={() =>
            setViewInfo((v) => ({
              ...v,
              month: isNextMonth ? viewInfo.month : viewInfo.month + 1,
            }))
          }>
          <ChevronRightIcon className="w-8 h-8 fill-current text-gray-500 cursor-pointer" />
        </button>
      </div>
      <div className="flex">
        {weekDays.map((day, i) => (
          <div
            className="text-xs h-[44px] leading-[44px] grow shrink-0 basis-[14%] flex items-center justify-center"
            key={i}>
            {day.toLocaleUpperCase()}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap">
        {monthDays.map((value) => {
          const monthLastDay = filteredMonthDays[filteredMonthDays.length - 1];
          const lastItem = monthDays[monthDays.length - 1];

          if (showSimpleDay) {
            return (
              <Day
                key={value.key}
                value={value}
                monthLastDay={monthLastDay}
                lastItem={lastItem}
                rangeValue={rangeValue}
                isOneDate={isOneDate}
                onRangeValueChange={onRangeValueChange}
              />
            );
          }

          const dateInfos = getDateInfos({
            date: value.date,
            futureBookings: futureBookings || [],
            priceVariants: priceVariants || [],
          });

          return (
            <BookingDay
              key={value.key}
              currentDate={currentDate}
              value={value}
              dateInfos={dateInfos}
              monthLastDay={monthLastDay}
              lastItem={lastItem}
              rangeValue={rangeValue}
              isOneDate={isOneDate}
              limitDate={limitDate}
              priceVariants={priceVariants}
              setLimitDate={setLimitDate}
              onRangeValueChange={onRangeValueChange}
              extraDicts={extraDicts}
              futureBookings={futureBookings}
              skipCheckInDaysValidation={skipCheckInDaysValidation}
              atPricePreview={atPricePreview}
            />
          );
        })}
      </div>
    </div>
  );
}
